import { ROWS_PER_PAGE_OPTIONS } from '@app/src/wf-constants'
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined'
import FullscreenExitOutlinedIcon from '@mui/icons-material/FullscreenExitOutlined'
import FullscreenOutlinedIcon from '@mui/icons-material/FullscreenOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined'
import { alpha, darken, lighten, Stack, type Theme } from '@mui/material'
import type { MRT_DensityState, MRT_Row, MRT_TableInstance, MRT_TableOptions } from 'material-react-table'
import { MRT_TablePagination as MRTTablePagination } from 'material-react-table'
import React, { type RefObject } from 'react'
import type { MrtRowData, MrtRowSizing } from './mrtCollectionTypes'
import { MrtRowSelectCheckbox } from './MrtSelectionCheckbox'
import { TABLE_CONTAINER_MAX_HEIGHT_VAR } from './useMeasuredTableContainerHeight'

/**
 * Row height per density level. The global MuiTableCell theme pins cell height, so we set this
 * explicitly to make MRT's density toggle actually change row size. Font size is intentionally kept
 * constant across densities (see muiTableBodyCellProps) so every cell shares one size.
 */
const getRowHeight = (density: MRT_DensityState, theme: Theme): string =>
  ({
    compact: theme.spacing(5),
    comfortable: theme.spacing(7),
    spacious: theme.spacing(9),
  })[density]

/** Vertical padding per density, which is what density changes on a row the content has made tall. */
const getCellPaddingY = (density: MRT_DensityState): number => ({ compact: 1, comfortable: 2, spacious: 3 })[density]

/**
 * A body cell is a flex item (MRT runs grid layout because columns are resizable), so a definite
 * height is never stretched and taller content is cropped by the cell's overflow. Content rows
 * therefore need 'auto' spelled out: leaving the height off instead lets the global MuiTableCell
 * theme pin the cell back to 72px. The density height becomes their floor, and since content taller
 * than the floor ignores it, density also sets the padding so the toggle keeps an effect.
 */
export const getCellSizingSx = (rowSizing: MrtRowSizing, density: MRT_DensityState, theme: Theme) =>
  rowSizing === 'content'
    ? {
        height: 'auto',
        minHeight: getRowHeight(density, theme),
        py: getCellPaddingY(density),
        alignItems: 'flex-start',
        verticalAlign: 'top',
      }
    : { height: getRowHeight(density, theme), py: 0, alignItems: 'center', verticalAlign: 'middle' }

const CELL_OVERLAY = {
  content: '""',
  height: '100%',
  left: 0,
  position: 'absolute',
  top: 0,
  width: '100%',
  zIndex: -1,
} as const

const ROW_SELECTED_CHECKBOX = 'input[type="checkbox"]:checked'

type MrtTableOptionsArgs<T extends MrtRowData> = {
  getRowId: (row: T, index: number, parentRow?: MRT_Row<T>) => string
  tableContainerRef: RefObject<HTMLDivElement>
  getSubRows?: (row: T) => T[] | undefined
  enableRowSelection?: boolean | ((row: MRT_Row<T>) => boolean)
  hasCustomSelectionUi?: boolean
  defaultExpanded?: boolean
  rowSizing?: MrtRowSizing
  scrollWithPage?: boolean
  // Keeps the toolbar with its filter bar but drops the rows and the pagination under it.
  hideTableBody?: boolean
}

/**
 * The presentation half of the MRT config: feature toggles, layout and all the styling. Kept separate
 * from the scene so the look of the table can be tweaked in one place. Behaviour (data, state, change
 * handlers, selection) is wired in the scene.
 */
export const getMrtTableOptions = <T extends MrtRowData>({
  getRowId,
  tableContainerRef,
  getSubRows,
  enableRowSelection = true,
  hasCustomSelectionUi = false,
  defaultExpanded = false,
  rowSizing = 'fixed',
  scrollWithPage = false,
  hideTableBody = false,
}: MrtTableOptionsArgs<T>): Partial<MRT_TableOptions<T>> => ({
  getRowId,
  getSubRows,
  enableExpanding: Boolean(getSubRows),
  enableExpandAll: Boolean(getSubRows),
  ...(defaultExpanded && { initialState: { expanded: true } }),
  displayColumnDefOptions: {
    'mrt-row-expand': { size: 50 },
    'mrt-row-select': hasCustomSelectionUi
      ? { size: 0, minSize: 0, Cell: () => null, Header: () => null }
      : {
          Cell: ({
            row,
            staticRowIndex,
            table,
          }: {
            row: MRT_Row<T>
            staticRowIndex?: number
            table: MRT_TableInstance<T>
          }) => <MrtRowSelectCheckbox row={row} staticRowIndex={staticRowIndex} table={table} />,
        },
  },
  enableRowSelection,
  enableColumnOrdering: true,
  enableColumnDragging: false,
  enableColumnFilters: false,
  enableColumnPinning: true,
  enableGlobalFilter: true,
  enableFilterMatchHighlighting: true,
  enableColumnResizing: true,
  columnResizeMode: 'onEnd',
  defaultColumn: { size: 240, minSize: 80 },
  icons: {
    SearchIcon: SearchOutlinedIcon,
    ViewColumnIcon: ViewColumnOutlinedIcon,
    FullscreenIcon: FullscreenOutlinedIcon,
    FullscreenExitIcon: FullscreenExitOutlinedIcon,
    FilterListIcon: FilterListOutlinedIcon,
  },
  // Also what gives the container its own max height, so it goes when the page scrolls instead.
  enableStickyHeader: !scrollWithPage,
  enableBottomToolbar: !hideTableBody,
  muiPaginationProps: { rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS },
  // The Intercom launcher floats over the bottom-right corner and covers the pagination MRT puts
  // there, so render it centred in the bottom toolbar instead of in MRT's own slot.
  positionPagination: 'none',
  renderBottomToolbarCustomActions: ({ table }) => (
    <Stack direction="row" justifyContent="center" width="100%">
      <MRTTablePagination table={table} />
    </Stack>
  ),
  manualFiltering: true,
  manualSorting: true,
  manualPagination: true,
  positionToolbarAlertBanner: 'none',
  positionToolbarDropZone: 'none',
  // Sub-rows are distinguished by their own columns, not depth - skip MRT's per-depth indent.
  muiExpandButtonProps: { sx: { ml: 0 } },
  muiSearchTextFieldProps: {
    sx: (theme: Theme) => ({ minWidth: theme.spacing(30), mr: 1 }),
  },
  muiColumnActionsButtonProps: {
    sx: {
      width: 32,
      height: 32,
      opacity: 0.6,
      '& svg': { transform: 'none', fontSize: 20 },
    },
  },
  renderColumnActionsMenuItems: ({ internalColumnMenuItems }) =>
    internalColumnMenuItems.map(menuItem => {
      if (!React.isValidElement(menuItem)) return menuItem
      const element = menuItem as React.ReactElement<{ sx?: unknown }>
      const existingSx = element.props.sx
      return React.cloneElement(element, {
        sx: [
          ...(Array.isArray(existingSx) ? existingSx : [existingSx]),
          { '& .MuiListItemIcon-root svg': { fontSize: 20 } },
        ],
      })
    }),
  muiSelectCheckboxProps: { sx: { ml: 1 } },
  muiTopToolbarProps: {
    sx: {
      minHeight: 0,
      py: 1,
      px: 1,
      backgroundColor: 'grey.100',
      '& > .MuiBox-root': { alignItems: 'center', flexWrap: 'wrap', rowGap: 1 },
    },
  },
  muiBottomToolbarProps: {
    sx: (theme: Theme) => ({
      minHeight: theme.spacing(5),
      backgroundColor: 'grey.100',
    }),
  },
  muiTablePaperProps: {
    elevation: 0,
    sx: {
      // Takes the height left in the viewport and clips, unless the page scrolls: then it grows. Without
      // rows the paper is just the toolbar, so it keeps its height rather than giving it up to whatever is
      // shown in their place.
      ...(scrollWithPage ? {} : { flex: hideTableBody ? '0 0 auto' : 1, overflow: 'hidden' }),
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      boxShadow: 'none',
      borderRadius: 0,
      backgroundColor: 'grey.100',
    },
  },
  muiTableProps: {
    sx: { backgroundColor: 'grey.100' },
  },
  // Full screen brings its own scroll, so the measured height goes there too.
  muiTableContainerProps: ({ table }) => ({
    ref: tableContainerRef,
    sx: {
      // Hidden rather than unmounted, so the measured container ref survives a switch back.
      ...(hideTableBody && { display: 'none' }),
      ...(scrollWithPage ? {} : { flex: 1 }),
      minHeight: 0,
      maxHeight: scrollWithPage || table.getState().isFullScreen ? undefined : `var(${TABLE_CONTAINER_MAX_HEIGHT_VAR})`,
      boxShadow: 'none',
      backgroundColor: 'grey.100',
    },
  }),
  muiTableHeadRowProps: {
    sx: theme => ({
      backgroundColor: 'grey.100',
      '& .MuiTableCell-root': {
        height: theme.spacing(5.5),
      },
    }),
  },
  // Function form so we can read the sort state and keep the sort indicator visible only while the
  // column is actively sorted; otherwise the per-header controls appear only on hover.
  muiTableHeadCellProps: ({ column }) => {
    if (hasCustomSelectionUi && column.id === 'mrt-row-select') return { sx: { padding: 0, border: 'none' } }
    return {
      sx: (theme: Theme) => {
        const isSorted = Boolean(column.getIsSorted())
        const fade = theme.transitions.create('opacity')
        return {
          backgroundColor: 'grey.100',
          fontWeight: theme.typography.fontWeightMedium,
          // Explicit height to override the global MuiTableCell height (72px) and keep the header slim.
          height: theme.spacing(5.5),
          pt: 1,
          pb: 0,
          px: 1.5,
          borderBottom: '1px solid',
          borderColor: 'grey.300',
          '& .Mui-TableHeadCell-Content': {
            justifyContent: 'space-between',
            width: '100%',
            alignItems: 'flex-start',
          },
          // MRT only ellipsis-truncates header labels shorter than 20 chars and lets longer ones wrap
          // (and drops the ellipsis on hover). Force every header onto one truncated line to match the
          // body cells.
          '& .Mui-TableHeadCell-Content-Wrapper': {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            '&:hover': { textOverflow: 'ellipsis' },
          },
          // Hide the three-dots until the header cell is hovered.
          '& .Mui-TableHeadCell-Content-Actions': { opacity: 0, transition: fade },
          // Sort arrow: visible when actively sorted, otherwise revealed on hover.
          '& .MuiTableSortLabel-root': { opacity: isSorted ? 1 : 0, transition: fade },
          // Drop the default transform on the resize handle divider so it sits inside the cell and is revealed on hover.
          '& .Mui-TableHeadCell-ResizeHandle-Divider': {
            transform: 'none',
            borderColor: 'transparent',
            transition: theme.transitions.create('border-color'),
          },
          '&:hover': {
            '& .Mui-TableHeadCell-Content-Actions': { opacity: 1 },
            '& .MuiTableSortLabel-root': { opacity: 1 },
            '& .Mui-TableHeadCell-ResizeHandle-Divider': { borderColor: theme.palette.grey[400] },
          },
        }
      },
    }
  },
  muiTableBodyCellProps: ({ table, column }) => {
    if (hasCustomSelectionUi && column.id === 'mrt-row-select') return { sx: { padding: 0, border: 'none' } }
    const { density } = table.getState()
    return {
      sx: (theme: Theme) => ({
        ...getCellSizingSx(rowSizing, density, theme),
        px: 1.5,
        fontSize: theme.typography.body1.fontSize,
        '& .MuiTypography-root': { fontSize: 'inherit' },
        borderColor: 'grey.200',
        overflow: 'hidden',
        '& > *': { minWidth: 0, maxWidth: '100%' },
      }),
    }
  },
  muiTableBodyRowProps: ({ table }) => {
    const { density } = table.getState()
    const { baseBackgroundColor } = table.options.mrtTheme
    return {
      sx: (theme: Theme) => ({
        height: rowSizing === 'content' ? 'auto' : getRowHeight(density, theme),
        // MRT's spacer cell, which fills the width left of the last column, carries column-level props
        // that replace the ones above, so it keeps the theme's fixed cell height and draws its border
        // mid-row. Inheriting the row's height lines it up whichever height that is.
        '& > td:last-of-type': { height: 'inherit' },
        backgroundColor: theme.palette.background.default,
        'td:after': undefined,
        '&:hover td:after': {
          ...CELL_OVERLAY,
          backgroundColor: alpha(
            theme.palette.mode === 'dark' ? lighten(baseBackgroundColor, 0.3) : darken(baseBackgroundColor, 0.3),
            0.3,
          ),
        },
        [`&:has(${ROW_SELECTED_CHECKBOX}) td:after`]: { ...CELL_OVERLAY, backgroundColor: 'grey.200' },
        [`&:has(${ROW_SELECTED_CHECKBOX}):hover td:after`]: { backgroundColor: 'grey.300' },
      }),
    }
  },
})
